import { useEffect, useState } from 'react'
import { useAuthStore } from '@/store/auth'
import { useLocation, useNavigate } from 'react-router-dom'

export function AuthTestPage() {
  const { isAuthenticated, isInitializing, hasOAuthToken, checkAuth, user, token } = useAuthStore()
  const [diagnostics, setDiagnostics] = useState<string[]>([])
  const location = useLocation()
  const navigate = useNavigate()

  const addDiagnostic = (message: string) => {
    setDiagnostics(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`])
  }

  useEffect(() => {
    addDiagnostic('AuthTestPage mounted')
    addDiagnostic(`Current URL: ${window.location.href}`)
    addDiagnostic(`Location pathname: ${location.pathname}`)
    addDiagnostic(`Location search: ${location.search}`)
    
    const urlParams = new URLSearchParams(window.location.search)
    const oauthToken = urlParams.get('token')
    addDiagnostic(`OAuth token from URL: ${oauthToken ? 'YES' : 'NO'}`)
    
    if (oauthToken) {
      addDiagnostic(`Token preview: ${oauthToken.substring(0, 20)}...`)
    }
    
    addDiagnostic(`hasOAuthToken(): ${hasOAuthToken()}`)
    addDiagnostic(`isAuthenticated: ${isAuthenticated}`)
    addDiagnostic(`isInitializing: ${isInitializing}`)
    addDiagnostic(`user: ${user ? JSON.stringify(user) : 'null'}`)
    addDiagnostic(`token: ${token ? 'YES' : 'NO'}`)
    
    // Trigger auth check
    addDiagnostic('Triggering checkAuth...')
    checkAuth().then(() => {
      addDiagnostic('checkAuth completed')
    }).catch((error) => {
      addDiagnostic(`checkAuth error: ${error.message}`)
    })
  }, [])

  // Monitor auth state changes
  useEffect(() => {
    addDiagnostic(`Auth state changed - isAuthenticated: ${isAuthenticated}, isInitializing: ${isInitializing}`)
  }, [isAuthenticated, isInitializing])

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">Authentication Diagnostics</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Current State */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-orange-400">Current State</h2>
            <div className="space-y-2 text-sm">
              <div><span className="font-semibold">URL:</span> {window.location.href}</div>
              <div><span className="font-semibold">Authenticated:</span> <span className={isAuthenticated ? 'text-green-400' : 'text-red-400'}>{isAuthenticated ? 'YES' : 'NO'}</span></div>
              <div><span className="font-semibold">Initializing:</span> <span className={isInitializing ? 'text-yellow-400' : 'text-gray-400'}>{isInitializing ? 'YES' : 'NO'}</span></div>
              <div><span className="font-semibold">Has OAuth Token:</span> <span className={hasOAuthToken() ? 'text-green-400' : 'text-red-400'}>{hasOAuthToken() ? 'YES' : 'NO'}</span></div>
              <div><span className="font-semibold">User:</span> {user ? user.username : 'None'}</div>
              <div><span className="font-semibold">Token:</span> {token ? 'Present' : 'None'}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-orange-400">Actions</h2>
            <div className="space-y-4">
              <button
                onClick={() => checkAuth()}
                className="w-full bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded transition-colors"
              >
                Trigger checkAuth()
              </button>
              <button
                onClick={() => navigate('/chat')}
                className="w-full bg-green-600 hover:bg-green-700 px-4 py-2 rounded transition-colors"
              >
                Go to Chat
              </button>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-red-600 hover:bg-red-700 px-4 py-2 rounded transition-colors"
              >
                Go to Login
              </button>
              <button
                onClick={() => {
                  localStorage.clear()
                  window.location.reload()
                }}
                className="w-full bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded transition-colors"
              >
                Clear Storage & Reload
              </button>
            </div>
          </div>
        </div>

        {/* Diagnostics Log */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4 text-orange-400">Diagnostics Log</h2>
          <div className="bg-black rounded p-4 max-h-96 overflow-y-auto">
            {diagnostics.map((diagnostic, index) => (
              <div key={index} className="text-sm text-gray-300 font-mono mb-1">
                {diagnostic}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
} 